import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Target, Users, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Football = () => {
  const photos = [
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/5expjl47_IMG_7110.jpeg',
      title: 'Coach Massa',
      description: 'Démonstration technique'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/izhldjwx_IMG_7111.jpeg',
      title: 'Coaching Personnalisé',
      description: 'Suivi individualisé'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/wguw9m7g_IMG_7112.jpeg',
      title: 'Entraînement Groupe',
      description: 'Formation collective'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/69otobva_IMG_7108.jpeg',
      title: 'Technique Avancée',
      description: 'Perfectionnement'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_em-fitness/artifacts/7on43x8h_IMG_7110.jpeg"
            alt="Football Academy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-6">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            EM COACHING
            <span className="text-red-600 block">FOOTBALL ACADEMY</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
            Formation football professionnelle pour tous niveaux. 
            Apprenez avec un ancien footballeur professionnel.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg group">
              Rejoindre l'Academy
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      {/* About Football Academy */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Formation par un <span className="text-red-600">Pro</span>
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              Coach Massa, ancien footballeur professionnel avec plus de 17 ans d'expérience dans le sport, 
              transmet sa passion et son expertise à travers la Football Academy. Il partage la discipline, 
              la rigueur et la mentalité de gagnant acquises au plus haut niveau.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-16 max-w-3xl mx-auto">
            {[
              {
                icon: Trophy,
                title: 'Expérience Pro',
                description: 'Ancien footballeur professionnel'
              },
              {
                icon: Target,
                title: 'Tous Niveaux',
                description: 'Du débutant au joueur confirmé'
              },
              {
                icon: Users,
                title: 'Tous Âges',
                description: 'Enfants, ados et adultes'
              },
              {
                icon: TrendingUp,
                title: 'Progression',
                description: 'Programme évolutif personnalisé'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-zinc-900 p-6 rounded-lg text-center hover:bg-zinc-800 transition-all hover:scale-105"
              >
                <feature.icon className="w-10 h-10 text-red-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-white text-center mb-4">
            L'Academy en <span className="text-red-600">Action</span>
          </h2>
          <p className="text-gray-400 text-center mb-16 text-lg">
            Découvrez notre méthode d'entraînement professionnelle
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {photos.map((photo, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl bg-zinc-900 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={photo.url}
                    alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-white font-bold text-xl mb-2">{photo.title}</h3>
                    <p className="text-gray-300">{photo.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Programme de <span className="text-red-600">Formation</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-zinc-900 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-4">Technique Individuelle</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Maîtrise du ballon et contrôle</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Dribbles et feintes</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Passes et réception</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Frappe et finition</span>
                  </li>
                </ul>
              </div>

              <div className="bg-zinc-900 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-4">Tactique & Jeu Collectif</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Positionnement sur le terrain</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Lecture du jeu</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Jeu sans ballon</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Communication et esprit d'équipe</span>
                  </li>
                </ul>
              </div>

              <div className="bg-zinc-900 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-4">Préparation Physique</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Endurance et cardio</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Vitesse et explosivité</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Force et puissance</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Agilité et coordination</span>
                  </li>
                </ul>
              </div>

              <div className="bg-zinc-900 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-4">Mental & Attitude</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Mentalité de gagnant</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Discipline et rigueur</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Gestion du stress</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Confiance en soi</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à Rejoindre l'Academy ?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Que vous soyez débutant ou joueur confirmé, enfant ou adulte, 
            la Football Academy vous accueille pour développer votre talent
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-black hover:bg-zinc-900 text-white px-8 py-6 text-lg">
              Réserver un Essai
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Football;
